import React, { useEffect, useRef, useState } from 'react';
import { X, Minus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PROPERTY_TYPES, EMIRATES, PRIORITIES, SOURCES, STAGES, DEAL_TYPES } from '@/lib/constants';
import { tomorrow9amISO } from '@/lib/format';

export default function ContactSlideOver({ open, onClose, contact, onSaved, onDeleted, agents = [] }) {
    const { profile } = useAuth();
    const isEdit = !!contact?.id;
    const [form, setForm] = useState(blank());
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [minimised, setMinimised] = useState(false);
    const initKeyRef = useRef(null);

    function blank() {
        return {
            full_name: '', phone: '', whatsapp: '', email: '', nationality: '',
            budget_aed: '', property_type: '', bedrooms_preference: '', emirate_preference: '',
            source: 'Walk-in', source_detail: '', priority: 'Warm', pipeline_stage: 'New', deal_type: '',
            assigned_to: '',
            next_followup_at: '', followup_notes: '', notes: '',
        };
    }

    useEffect(() => {
        if (!open) return;
        setMinimised(false);
        const key = contact?.id ?? '__new__';
        if (key === initKeyRef.current) return;
        initKeyRef.current = key;
        if (contact?.id) {
            const assignedTo = typeof contact.assigned_to === 'string'
                ? contact.assigned_to
                : (contact.assigned_to?.id || '');
            setForm({ ...blank(), ...contact, assigned_to: assignedTo, next_followup_at: contact.next_followup_at?.slice(0, 16) || '' });
        } else setForm({ ...blank(), next_followup_at: tomorrow9amISO().slice(0, 16), assigned_to: profile?.id || '' });
    }, [open, contact?.id]);

    if (!open) return null;

    if (minimised) {
        return (
            <div className="fixed bottom-4 right-4 z-[60]">
                <div className="bg-white border border-gray-200 rounded-lg shadow-xl px-4 py-3 flex items-center gap-3 min-w-[220px] max-w-xs">
                    <span className="text-[13px] font-semibold text-aks-text flex-1 truncate">
                        {isEdit ? (contact?.full_name || 'Edit contact') : 'New Lead'}
                    </span>
                    <button type="button" onClick={() => setMinimised(false)} className="text-[12px] font-semibold text-aks-navy hover:text-aks-gold">
                        Expand
                    </button>
                    <button type="button" onClick={() => { initKeyRef.current = null; onClose(); }} className="text-gray-400 hover:text-aks-text ml-1">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            payload.budget_aed = payload.budget_aed ? Number(payload.budget_aed) : null;
            payload.bedrooms_preference = payload.bedrooms_preference ? Number(payload.bedrooms_preference) : null;
            payload.next_followup_at = payload.next_followup_at ? new Date(payload.next_followup_at).toISOString() : null;
            // strip empty strings
            for (const k of Object.keys(payload)) if (payload[k] === '') payload[k] = null;

            if (isEdit) {
                const updatePayload = {
                    full_name: payload.full_name,
                    phone: payload.phone,
                    whatsapp: payload.whatsapp,
                    email: payload.email,
                    nationality: payload.nationality,
                    budget_aed: payload.budget_aed,
                    property_type: payload.property_type,
                    bedrooms_preference: payload.bedrooms_preference,
                    emirate_preference: payload.emirate_preference,
                    source: payload.source,
                    source_detail: payload.source_detail,
                    priority: payload.priority,
                    pipeline_stage: payload.pipeline_stage,
                    deal_type: payload.deal_type,
                    next_followup_at: payload.next_followup_at,
                    followup_notes: payload.followup_notes,
                    notes: payload.notes,
                    client_type: payload.client_type,
                    total_transaction_value_aed: payload.total_transaction_value_aed,
                    lease_start_date: payload.lease_start_date,
                    lease_end_date: payload.lease_end_date,
                    monthly_rent_aed: payload.monthly_rent_aed,
                    golden_visa_eligible: payload.golden_visa_eligible,
                    assigned_to: form.assigned_to || null,
                };
const { data: updated, error } = await supabase.from('pipeline_contacts').update(updatePayload).eq('id', contact.id).select().single();
                if (error) throw error;
                toast.success('Contact updated');
                initKeyRef.current = null;
                await onSaved?.(updated);
            } else {
                payload.organization_id = profile.organization_id;
                payload.created_by = profile.id;
                if (!payload.assigned_to) payload.assigned_to = profile.id;
                const { data, error } = await supabase.from('pipeline_contacts').insert(payload).select().single();
                if (error) throw error;
                await supabase.from('pipeline_activities').insert({
                    contact_id: data.id, organization_id: profile.organization_id, agent_id: profile.id,
                    activity_type: 'note', description: `Contact added by ${profile.full_name || 'user'}`,
                });
                toast.success('Contact added');
                initKeyRef.current = null;
                await onSaved?.();
            }
            onClose();
        } catch (err) {
            toast.error(err.message || 'Save failed');
        } finally { setSaving(false); }
    };

    const deleteContact = async () => {
        setSaving(true);
        try {
            const { error } = await supabase.from('pipeline_contacts').delete().eq('id', contact.id);
            if (error) throw error;
            toast.success('Contact deleted');
            initKeyRef.current = null;
            onClose();
            await onDeleted?.();
        } catch (err) {
            toast.error(err.message || 'Delete failed');
        } finally { setSaving(false); setConfirmDelete(false); }
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const isClient = ['Closed Won', 'Active Client', 'Renewal Due', 'Repeat Investor'].includes(form.pipeline_stage);

    return (
        <div className="fixed inset-0 z-[60] flex justify-end" onClick={() => setMinimised(true)}>
            <div className="absolute inset-0 bg-black/40" />
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="relative bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl" data-testid="contact-slideover">
                <div className="sticky top-0 bg-white border-b border-gray-100 p-5 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-xl font-bold text-aks-text">{isEdit ? 'Edit contact' : 'Add contact'}</h2>
                        <p className="text-[12px] text-gray-500">{isEdit ? contact.full_name : 'New pipeline entry'}</p>
                    </div>
                    <div className="flex items-center gap-1">
                        <button type="button" onClick={() => setMinimised(true)} className="text-gray-400 hover:text-aks-text" title="Minimise"><Minus className="h-5 w-5"/></button>
                        <button type="button" onClick={() => { initKeyRef.current = null; onClose(); }} className="text-gray-400 hover:text-aks-text"><X className="h-5 w-5"/></button>
                    </div>
                </div>
                <div className="p-5 space-y-5">
                    <Section title="Identity">
                        <Field label="Full name *"><input required value={form.full_name || ''} onChange={set('full_name')} className="input" data-testid="so-name" /></Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Phone *"><input required value={form.phone || ''} onChange={set('phone')} className="input" data-testid="so-phone" /></Field>
                            <Field label="WhatsApp"><input value={form.whatsapp || ''} onChange={set('whatsapp')} className="input" /></Field>
                            <Field label="Email"><input type="email" value={form.email || ''} onChange={set('email')} className="input" /></Field>
                            <Field label="Nationality"><input value={form.nationality || ''} onChange={set('nationality')} className="input" /></Field>
                        </div>
                    </Section>
                    <Section title="Property interest">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Budget (AED)"><input type="number" value={form.budget_aed || ''} onChange={set('budget_aed')} className="input" /></Field>
                            <Field label="Bedrooms"><input type="number" value={form.bedrooms_preference || ''} onChange={set('bedrooms_preference')} className="input" /></Field>
                            <Field label="Property type"><select value={form.property_type || ''} onChange={set('property_type')} className="input capitalize"><option value="">—</option>{PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
                            <Field label="Emirate"><select value={form.emirate_preference || ''} onChange={set('emirate_preference')} className="input"><option value="">—</option>{EMIRATES.map((e) => <option key={e} value={e}>{e}</option>)}</select></Field>
                        </div>
                    </Section>
                    <Section title="Pipeline">
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Source"><select value={form.source || ''} onChange={set('source')} className="input">{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                            <Field label="Priority"><select value={form.priority || ''} onChange={set('priority')} className="input">{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
                            {form.source === 'Other' && (
                                <div className="col-span-2">
                                    <Field label="Source detail"><input value={form.source_detail || ''} onChange={set('source_detail')} placeholder="e.g. Instagram, Bayut, Event…" className="input" /></Field>
                                </div>
                            )}
                            <Field label="Stage"><select value={form.pipeline_stage || 'New'} onChange={set('pipeline_stage')} className="input">{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
                            <Field label="Deal type"><select value={form.deal_type || ''} onChange={set('deal_type')} className="input"><option value="">—</option>{DEAL_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}</select></Field>
                            <Field label="Assigned agent">
                                <select value={form.assigned_to || ''} onChange={set('assigned_to')} className="input" disabled={profile?.role !== 'admin'}>
                                    <option value="">Unassigned</option>
                                    {agents.map((a) => <option key={a.id} value={a.id}>{a.full_name || a.email}</option>)}
                                </select>
                            </Field>
                        </div>
                    </Section>
                    <Section title="Follow-up">
                        <Field label="Next follow-up"><input type="datetime-local" value={form.next_followup_at || ''} onChange={set('next_followup_at')} className="input" /></Field>
                        <Field label="Follow-up notes"><textarea rows={2} value={form.followup_notes || ''} onChange={set('followup_notes')} className="input" /></Field>
                    </Section>
                    {isClient && (
                        <Section title="Client details">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Client type"><select value={form.client_type || ''} onChange={set('client_type')} className="input capitalize"><option value="">—</option><option value="buyer">buyer</option><option value="tenant">tenant</option><option value="investor">investor</option></select></Field>
                                <Field label="Total transaction value"><input type="number" value={form.total_transaction_value_aed || ''} onChange={set('total_transaction_value_aed')} className="input" /></Field>
                                <Field label="Lease start"><input type="date" value={form.lease_start_date || ''} onChange={set('lease_start_date')} className="input" /></Field>
                                <Field label="Lease end"><input type="date" value={form.lease_end_date || ''} onChange={set('lease_end_date')} className="input" /></Field>
                                <Field label="Monthly rent"><input type="number" value={form.monthly_rent_aed || ''} onChange={set('monthly_rent_aed')} className="input" /></Field>
                                <Field label="Golden visa eligible"><select value={form.golden_visa_eligible ? 'yes' : 'no'} onChange={(e) => setForm((f) => ({ ...f, golden_visa_eligible: e.target.value === 'yes' }))} className="input"><option value="no">No</option><option value="yes">Yes</option></select></Field>
                            </div>
                        </Section>
                    )}
                    <Section title="Notes">
                        <textarea rows={3} value={form.notes || ''} onChange={set('notes')} className="input" />
                    </Section>
                </div>
                <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 space-y-2">
                    {isEdit && !confirmDelete && (
                        <button type="button" onClick={() => setConfirmDelete(true)} className="w-full h-9 rounded-md border border-red-300 text-red-600 text-[13px] font-medium hover:bg-red-50 transition">Delete contact</button>
                    )}
                    {isEdit && confirmDelete && (
                        <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200">
                            <span className="flex-1 text-[13px] text-red-700 font-medium">Delete this contact permanently?</span>
                            <button type="button" onClick={() => setConfirmDelete(false)} className="h-8 px-3 rounded-md border border-gray-300 text-[12px] font-medium hover:bg-white">Cancel</button>
                            <button type="button" onClick={deleteContact} disabled={saving} className="h-8 px-3 rounded-md bg-red-600 text-white text-[12px] font-semibold hover:bg-red-700 disabled:opacity-60">Delete</button>
                        </div>
                    )}
                    <div className="flex gap-3">
                        <button type="button" onClick={() => setMinimised(true)} className="flex-1 h-11 rounded-md border border-gray-300 text-[14px] font-medium hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={saving} className="flex-1 h-11 rounded-md bg-aks-navy text-white text-[14px] font-semibold hover:bg-aks-navy-2 disabled:opacity-60 aks-focus-ring" data-testid="so-submit">{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add to pipeline'}</button>
                    </div>
                </div>
                <style>{`.input{margin-top:.25rem;width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #d1d5db;border-radius:.375rem;outline:none;font-size:14px;background:#fff}.input:focus{border-color:#C9A84C;box-shadow:0 0 0 3px rgba(201,168,76,.25)}textarea.input{height:auto;padding:.5rem .75rem}`}</style>
            </form>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">{title}</div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}
function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[12px] font-medium text-aks-text">{label}</span>
            {children}
        </label>
    );
}
